// @flow
import { useState } from 'preact/hooks';
import axios, {
  AxiosResponse,
  AxiosError,
  Method,
  AxiosRequestConfig,
  AxiosInstance,
} from 'axios';

const instance = axios.create({
  baseURL: process.env.PREACT_APP_BE_ENDPOINT_BASE_URL,
});

export type RequestInterceptorType = (
  config: AxiosRequestConfig,
) => AxiosRequestConfig;
export type ResponseInterceptorType = (
  response: AxiosResponse,
) => AxiosResponse;

export const addRequestInterceptor = (interceptor: RequestInterceptorType) => {
  instance.interceptors.request.use(interceptor);
};

export const removeRequestInterceptor = (
  interceptor: RequestInterceptorType,
) => {
  instance.interceptors.request.eject(interceptor);
};

export const addResponseInterceptor = (interceptor: Function) => {
  instance.interceptors.response.use(interceptor);
};

export const removeResponseInterceptor = (interceptor: Function) => {
  instance.interceptors.response.eject(interceptor);
};

export type Request<RequestType = any> = {
  path: string,
  method: Method,
  headers?: { [key: string]: string },
  params?: RequestType,
  data?: RequestType,
};

export type ResponseState<ResponseType = any, ResponseErrorType = any> = {
  responseHeaders?: { [key: string]: string },
  status?: number,
  value?: ResponseType,
  error?: ResponseErrorType,
  success?: boolean,
  loading: boolean,
};

export type Response<
  RequestType,
  ResponseType,
  ResponseErrorType,
> = ResponseState<ResponseType, ResponseErrorType> & {
  makeRequest: (
    requestConfig: Request<RequestType>,
  ) => Promise<ResponseState<ResponseType, ResponseErrorType>>,
  resetState: () => void,
};

const requests = (requestConfig: Request<any>, instance: AxiosInstance) => {
  return instance
    .request({ ...requestConfig, url: requestConfig.path })
    .then(responseBody);
};

const responseBody = (response: AxiosResponse) => response;

export function useRequest<
  RequestType,
  ResponseType,
  ResponseErrorType,
>(): Response<RequestType, ResponseType, ResponseErrorType> {
  const initialState: ResponseState<> = {
    loading: false,
  };

  const [state, setState] = useState(initialState);

  const resetState = () => {
    setState(initialState);
  };

  const makeRequest = async (
    requestConfig: Request<RequestType>,
  ): Promise<ResponseState<ResponseType, ResponseErrorType>> => {
    if (
      !requestConfig.path ||
      !requestConfig.path.trim() ||
      !requestConfig.method ||
      !requestConfig.method.trim()
    )
      return initialState;

    setState((old) => ({ ...old, loading: true }));

    try {
      const response = await requests(requestConfig, instance);

      const newState: ResponseState<> = {
        responseHeaders: response.headers,
        status: response.status,
        value: response.data,
        error: undefined,
        success: true,
        loading: false,
      };
      setState(newState);
      return newState;
    } catch (e) {
      const error: AxiosError = e;
      const newCatchState: ResponseState<> = {
        ...(error &&
          error.response && {
            responseHeaders: error.response.headers,
            status: error.response.status,
            value: undefined,
            error: error.response.data,
          }),
        success: false,
        loading: false,
      };
      setState(newCatchState);
      return newCatchState;
    }
  };

  return { ...state, makeRequest, resetState };
}



// WEBPACK FOOTER //
// ./api/services/useRequest.js